// Shared parsing/serialization for the product availability & conditions config.
// The config is stored as JSONB columns on `product`, reusing the same shapes
// the `service` table uses (operating_hours, restrictions, settings.intervals,
// booking_options) so admin and apps share one mental model.

import type {
  Product,
  ProductBookingOption,
  ProductTimeRange,
} from '../../hooks/useProducts';

export type DayKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
export const DAYS: { key: DayKey; label: string; long: string }[] = [
  { key: 'mon', label: 'Mon', long: 'monday' },
  { key: 'tue', label: 'Tue', long: 'tuesday' },
  { key: 'wed', label: 'Wed', long: 'wednesday' },
  { key: 'thu', label: 'Thu', long: 'thursday' },
  { key: 'fri', label: 'Fri', long: 'friday' },
  { key: 'sat', label: 'Sat', long: 'saturday' },
  { key: 'sun', label: 'Sun', long: 'sunday' },
];

export interface DayRanges { closed: boolean; ranges: ProductTimeRange[] }

export interface TierForm {
  id: string;
  label_en: string;
  label_es: string;
  price: string;
  currency: string;
  min_amount: string;
  max_amount: string;
}

export interface BlackoutForm { start: string; end: string; reason: string }

export interface ProductConfigForm {
  enableSchedule: boolean;
  schedule: Record<DayKey, DayRanges>;
  slotDuration: string; // minutes
  slotInterval: string; // minutes
  maxPerSlot: string;
  maxCapacity: string;  // people per day
  minParty: string;
  maxParty: string;
  minAge: string;
  requiresId: boolean;
  minAdvanceHours: string;
  maxAdvanceDays: string;
  blackouts: BlackoutForm[];
  tiers: TierForm[];
  images: string[];
  categories: string[];
}

const DEFAULT_RANGE: ProductTimeRange = { open: '09:00', close: '18:00' };

// ── ISO 8601 duration helpers ───────────────────────────────────────────────
export function minutesToISO(min: number | string | undefined): string | undefined {
  const n = typeof min === 'string' ? parseInt(min, 10) : min;
  return n && n > 0 ? `PT${n}M` : undefined;
}
export function hoursToISO(h: number | string | undefined): string | undefined {
  const n = typeof h === 'string' ? parseInt(h, 10) : h;
  return n && n > 0 ? `PT${n}H` : undefined;
}
export function daysToISO(d: number | string | undefined): string | undefined {
  const n = typeof d === 'string' ? parseInt(d, 10) : d;
  return n && n > 0 ? `P${n}D` : undefined;
}
export function isoToMinutes(v: string | undefined): string {
  if (!v) return '';
  const m = String(v).toUpperCase().match(/^P(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?$/);
  if (!m) return /^\d+$/.test(String(v)) ? String(v) : '';
  const days = +(m[1] || 0), hours = +(m[2] || 0), mins = +(m[3] || 0);
  return String(days * 1440 + hours * 60 + mins || '');
}
export function isoToHours(v: string | undefined): string {
  const min = isoToMinutes(v);
  return min ? String(Math.round(+min / 60)) : '';
}
export function isoToDays(v: string | undefined): string {
  const min = isoToMinutes(v);
  return min ? String(Math.round(+min / 1440)) : '';
}

// Coerce any stored time token ("8:00 AM", "08:00") to 24h "HH:mm".
function toInputTime(raw: string): string {
  const m = (raw || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!m) return raw || '';
  let h = parseInt(m[1], 10);
  const mer = (m[3] || '').toUpperCase();
  if (mer === 'PM' && h < 12) h += 12;
  if (mer === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m[2]}`;
}

export function emptyProductConfig(): ProductConfigForm {
  const schedule = {} as Record<DayKey, DayRanges>;
  DAYS.forEach(({ key }) => { schedule[key] = { closed: true, ranges: [{ ...DEFAULT_RANGE }] }; });
  return {
    enableSchedule: false,
    schedule,
    slotDuration: '',
    slotInterval: '',
    maxPerSlot: '',
    maxCapacity: '',
    minParty: '',
    maxParty: '',
    minAge: '',
    requiresId: false,
    minAdvanceHours: '',
    maxAdvanceDays: '',
    blackouts: [],
    tiers: [],
    images: [],
    categories: [],
  };
}

// ── Product → form ──────────────────────────────────────────────────────────
export function productToConfig(product: Product | null): ProductConfigForm {
  const cfg = emptyProductConfig();
  if (!product) return cfg;

  const oh = product.operating_hours;
  if (oh && typeof oh === 'object') {
    cfg.enableSchedule = true;
    DAYS.forEach(({ key, long }) => {
      const val = oh[long];
      if (!val) { cfg.schedule[key] = { closed: true, ranges: [{ ...DEFAULT_RANGE }] }; return; }
      const arr = Array.isArray(val) ? val : [val];
      const ranges = arr
        .filter((r) => r && r.open && r.close)
        .map((r) => ({ open: toInputTime(r.open), close: toInputTime(r.close) }));
      cfg.schedule[key] = ranges.length
        ? { closed: false, ranges }
        : { closed: true, ranges: [{ ...DEFAULT_RANGE }] };
    });
  }

  const intervals = product.settings?.intervals;
  cfg.slotDuration = isoToMinutes(intervals?.duration) || isoToMinutes(product.duration);
  cfg.slotInterval = isoToMinutes(intervals?.duration);
  cfg.maxPerSlot = intervals?.max_per_slot != null ? String(intervals.max_per_slot) : '';
  cfg.maxCapacity = product.max_capacity != null ? String(product.max_capacity) : '';

  const r = product.restrictions || {};
  cfg.minParty = r.min_party_size != null ? String(r.min_party_size) : '';
  cfg.maxParty = r.max_party_size != null ? String(r.max_party_size) : '';
  cfg.minAge = r.min_age != null ? String(r.min_age) : '';
  cfg.requiresId = !!r.requires_id;
  cfg.blackouts = (r.availability || [])
    .filter((p) => p.status === 'closed')
    .map((p) => ({ start: String(p.start || '').slice(0, 10), end: String(p.end || '').slice(0, 10), reason: p.reason || '' }));

  cfg.minAdvanceHours = isoToHours(product.booking_window_start);
  cfg.maxAdvanceDays = isoToDays(product.booking_window_end);

  cfg.tiers = (product.booking_options || []).map((o) => ({
    id: o.id || '',
    label_en: o.label_en || '',
    label_es: o.label_es || '',
    price: o.price != null ? String(o.price) : '',
    currency: o.currency || 'USD',
    min_amount: o.min_amount != null ? String(o.min_amount) : '',
    max_amount: o.max_amount != null ? String(o.max_amount) : '',
  }));

  cfg.images = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  cfg.categories = Array.isArray(product.categories) ? product.categories.filter(Boolean) : [];
  return cfg;
}

// ── Form → product (partial, omits empty sections) ──────────────────────────
export function configToProduct(cfg: ProductConfigForm): Partial<Product> {
  const out: Partial<Product> = {};

  if (cfg.enableSchedule) {
    const oh: Record<string, ProductTimeRange[]> = {};
    DAYS.forEach(({ key, long }) => {
      const day = cfg.schedule[key];
      if (day.closed) return;
      const ranges = day.ranges.filter((r) => r.open && r.close).map((r) => ({ open: r.open, close: r.close }));
      if (ranges.length) oh[long] = ranges;
    });
    out.operating_hours = oh;
  } else {
    out.operating_hours = undefined;
  }

  const intervals: Record<string, unknown> = {};
  if (cfg.slotInterval) intervals.duration = minutesToISO(cfg.slotInterval);
  if (cfg.maxPerSlot) intervals.max_per_slot = parseInt(cfg.maxPerSlot, 10);
  out.settings = Object.keys(intervals).length ? { intervals } : undefined;
  out.duration = minutesToISO(cfg.slotDuration) || undefined;
  out.max_capacity = cfg.maxCapacity ? parseInt(cfg.maxCapacity, 10) : undefined;
  out.booking_window_start = hoursToISO(cfg.minAdvanceHours);
  out.booking_window_end = daysToISO(cfg.maxAdvanceDays);

  const restrictions: Product['restrictions'] = {};
  if (cfg.minAge) restrictions.min_age = parseInt(cfg.minAge, 10);
  if (cfg.requiresId) restrictions.requires_id = true;
  if (cfg.minParty) restrictions.min_party_size = parseInt(cfg.minParty, 10);
  if (cfg.maxParty) restrictions.max_party_size = parseInt(cfg.maxParty, 10);
  const blackouts = cfg.blackouts
    .filter((b) => b.start && b.end)
    .map((b) => ({ start: b.start, end: b.end, status: 'closed', reason: b.reason || undefined }));
  if (blackouts.length) restrictions.availability = blackouts;
  out.restrictions = Object.keys(restrictions).length ? restrictions : undefined;

  out.booking_options = cfg.tiers.length
    ? cfg.tiers
        .filter((t) => t.id || t.label_en || t.label_es)
        .map((t) => ({
          id: t.id || (t.label_en || 'tier').toLowerCase().replace(/\s+/g, '_'),
          label_en: t.label_en,
          label_es: t.label_es,
          price: t.price ? Number(t.price) : 0,
          currency: t.currency || 'USD',
          min_amount: t.min_amount ? parseInt(t.min_amount, 10) : undefined,
          max_amount: t.max_amount ? parseInt(t.max_amount, 10) : undefined,
        })) as ProductBookingOption[]
    : undefined;

  out.images = cfg.images.filter(Boolean).length ? cfg.images.filter(Boolean) : undefined;
  out.categories = cfg.categories.filter(Boolean).length ? cfg.categories.filter(Boolean) : undefined;
  return out;
}
